<template>
  <span class="m3-btn__anchor" :class="anchorClass" @click="onAnchorTap">
    <component
      ref="btnElRef"
      :is="rootTag"
      v-bind="{ ...rootAttrs, ...$attrs }"
      :class="rootClass"
      :disabled="isNativeDisabled || undefined"
      :aria-busy="loading || undefined"
      :aria-disabled="(disable || loading) && !isNativeDisabled || undefined"
      :tabindex="tabIndex"
      :aria-describedby="describedById"
      @click="onClick"
      @focusin="onFocusin"
      @focusout="onFocusout"
      @keydown.esc="onEscape"
    >
      <!-- State layer: M3 overlay that lights up on hover / focus / press. -->
      <span class="m3-btn__state-layer" aria-hidden="true" />

      <!-- Loading band: bouncing brand-gradient blur behind content. Label
           stays visible (no spinner swap), indeterminate progress is felt
           rather than read. -->
      <span v-if="loading" class="m3-btn__progress" aria-hidden="true" />

      <span class="m3-btn__content">
        <q-icon
          v-if="icon"
          :name="icon"
          class="m3-btn__icon m3-btn__icon--left"
        />
        <span v-if="hasLabel" class="m3-btn__label">
          <slot>{{ label }}</slot>
        </span>
        <q-icon
          v-if="iconRight"
          :name="iconRight"
          class="m3-btn__icon m3-btn__icon--right"
        />
      </span>

      <!-- Optional styled tooltip — opt-in via the `tooltip` prop, or
           auto-echoes the full label when the ellipsis fallback is active
           (so mouse users can always read the truncated text). Hover delay +
           mobile long-press come from Quasar; bubble themed below.
           Stands down while the disabled bubble is up: `isNativeDisabled` is
           true only for a real <button>, so a disabled router-link/anchor still
           fires hover events and would show BOTH this and the wrapper's reason
           at once. The reason wins — it says more than the label does. -->
      <q-tooltip
        v-if="effectiveTooltip && !showDisabledTooltip"
        ref="enabledTooltipRef"
        class="m3-btn__tooltip"
        anchor="bottom middle"
        self="top middle"
        :offset="[0, 8]"
        :delay="400"
      >
        {{ effectiveTooltip }}
      </q-tooltip>
    </component>

    <!-- sr-only description: aria-describedby target for screen readers and
         keyboard-only users (q-tooltip only triggers on hover). Skipped when
         the tooltip merely echoes the accessible name (collapsed header
         actions, the ellipsis echo) — a description equal to the name makes
         screen readers announce the button twice. -->
    <span
      v-if="tooltipAddsInformation"
      :id="tooltipDescId"
      class="m3-btn__sr-desc"
    >{{ effectiveTooltip }}</span>

    <!-- The disabled reason, for screen readers. The q-tooltip below is a
         VISUAL affordance and announces nothing on its own, so without this the
         explanation is hover-only - and call sites moved reasons OUT of
         `ariaLabel` into `disabledTooltip` precisely to stop the accessible
         name being a sentence. That trade only holds if the reason still
         reaches assistive tech. -->
    <span
      v-if="showDisabledTooltip"
      :id="disabledDescId"
      class="m3-btn__sr-desc"
    >{{ disabledTooltip }}</span>

    <!-- Disabled-state tooltip. A natively-disabled <button> suppresses its
         own hover/focus events, so the `tooltip` above can never show while
         disabled — exactly when a "why is this disabled?" hint is most useful.
         Anchor it to the (never-disabled) wrapper span instead. The wrapper is
         `display: contents` until this is active, so it has zero layout impact
         on the enabled button (the common case). -->
    <q-tooltip
      v-if="showDisabledTooltip"
      ref="disabledTooltipRef"
      class="m3-btn__tooltip q-tooltip--tap-ok"
      anchor="bottom middle"
      self="top middle"
      :offset="[0, 8]"
      :delay="400"
    >
      {{ disabledTooltip }}
    </q-tooltip>
  </span>
</template>

<script setup lang="ts">
/**
 * BaseButton — the M3 2026 button engine for EMALINO.
 *
 * Native <button> / <a> / <router-link> under the hood. No q-btn. Every
 * paint variant resolves to semantic tokens (see _design-tokens.scss §7.5),
 * so light/dark mode comes for free and re-skinning is a token-level change.
 *
 * Use BaseButton directly when you want to pick a variant explicitly;
 * otherwise reach for the intent-specific wrappers:
 *
 *   PrimaryButton      → variant="filled"          (primary CTA)
 *   TonalButton        → variant="tonal"           (secondary action)
 *   OutlineButton      → variant="outlined"        (alternative action)
 *   LinkButton         → variant="text"            (low-emphasis inline action)
 *   ElevatedButton     → variant="elevated"        (floating on a surface)
 *   DestructiveButton  → variant="destructive"     (delete / reject)
 *   GlassButton        → variant="glass"           (hero / dark backgrounds)
 *   WhiteButton        → variant="filled" + white  (CTA over a dark hero)
 *
 * For icon-only and FAB shapes, prefer the dedicated IconButton / Fab
 * components (different geometry, different sizing scale).
 *
 * 2026 motion language:
 *   - 180ms / cubic-bezier(0.2, 0, 0, 1) for paint transitions (M3 standard).
 *   - scale(0.97) on press for tactile feedback (not translateY).
 *   - Focus ring via outline + outline-offset (never `outline: none`).
 *   - Bouncing brand-gradient progress band while loading.
 *   - Respects `prefers-reduced-motion` (static low-opacity glow fallback).
 */

import { computed, onBeforeUnmount, ref, toRef, useAttrs, useId, useSlots } from 'vue';
import { QIcon, QTooltip } from 'quasar';
import type { RouteLocationRaw } from 'vue-router';
import { useButtonFit } from '../../../composables/useButtonFit';

export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonVariant =
  | 'filled'
  | 'tonal'
  | 'outlined'
  | 'text'
  | 'elevated'
  | 'destructive'
  | 'destructive-tonal'
  | 'success'
  | 'success-tonal'
  | 'warning'
  | 'warning-tonal'
  | 'glass';

interface Props {
  /** Vue-router target. When set, the button renders as <router-link>. */
  to?: RouteLocationRaw;
  /** External link target. When set (and `to` is not), renders as <a>. */
  href?: string;
  /** Native button type (ignored for anchors / router-links). */
  type?: 'button' | 'submit' | 'reset';
  /** Visual paint variant. */
  variant?: ButtonVariant;
  /** Height + padding scale. */
  size?: ButtonSize;
  /** Label text. Slot content takes precedence when provided. */
  label?: string;
  /** Styled hover/long-press tooltip. Omit to render no tooltip. */
  tooltip?: string;
  /**
   * Tooltip shown while the button is disabled or loading. A natively-disabled
   * `<button>` suppresses its own hover events, so `tooltip` cannot show then;
   * this one anchors to a wrapper span instead. Use it to explain WHY the
   * button is disabled (e.g. "Complete required fields first").
   */
  disabledTooltip?: string;
  /** Icon (Quasar icon name) shown before the label. */
  icon?: string;
  /** Icon (Quasar icon name) shown after the label. */
  iconRight?: string;
  /** When true, the bouncing progress band animates; label stays visible. */
  loading?: boolean;
  /** When true, the button is non-interactive and visually faded. */
  disable?: boolean;
  /** Stretch the button to fill its container. */
  fullWidth?: boolean;
  /** Render with a pill (fully-rounded) silhouette. */
  pill?: boolean;
  /** Open external `href` links in a new tab (adds noopener / noreferrer). */
  external?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'filled',
  size: 'md',
  loading: false,
  disable: false,
  fullWidth: false,
  pill: false,
  external: false,
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const slots = useSlots();

// Auto-fit: shrinks font, padding, gap, and icon when the label doesn't fit;
// restores all defaults when it does. Falls back to ellipsis at the 12px floor.
// isEllipsized drives effectiveTooltip so truncated text stays reachable.
const btnElRef = ref<unknown>(null);
const { isEllipsized } = useButtonFit(btnElRef, { label: toRef(props, 'label'), size: toRef(props, 'size') });

const rootTag = computed<'button' | 'a' | 'router-link'>(() => {
  if (props.to !== undefined) return 'router-link';
  if (props.href !== undefined) return 'a';
  return 'button';
});

const isNativeDisabled = computed(
  () => rootTag.value === 'button' && (props.disable || props.loading),
);

const tabIndex = computed<number | undefined>(() => {
  // Anchors and router-links can't be disabled at the attribute level;
  // pull them out of the tab order so disabled keyboard nav matches.
  if (rootTag.value !== 'button' && (props.disable || props.loading)) return -1;
  return undefined;
});

const rootAttrs = computed<Record<string, unknown>>(() => {
  const attrs: Record<string, unknown> = {};

  if (rootTag.value === 'button') {
    attrs.type = props.type;
  } else if (rootTag.value === 'router-link') {
    attrs.to = props.to;
  } else if (rootTag.value === 'a') {
    attrs.href = props.href;
    if (props.external) {
      attrs.target = '_blank';
      attrs.rel = 'noopener noreferrer';
    }
  }

  return attrs;
});

const rootClass = computed(() => [
  'm3-btn',
  `m3-btn--${props.variant}`,
  `m3-btn--size-${props.size}`,
  {
    'm3-btn--full-width': props.fullWidth,
    'm3-btn--pill': props.pill,
    'm3-btn--loading': props.loading,
    'm3-btn--disabled': props.disable && !props.loading,
    'm3-btn--icon-only': !hasLabel.value && (Boolean(props.icon) || Boolean(props.iconRight)),
  },
]);

const hasLabel = computed(() => Boolean(slots.default) || Boolean(props.label));

// A disabled/loading button cannot surface its own `tooltip` (no hover events),
// so when a `disabledTooltip` is supplied we anchor it to the wrapper span.
const showDisabledTooltip = computed(
  () => Boolean(props.disabledTooltip) && (props.disable || props.loading),
);

// The wrapper is transparent to layout (display:contents) until the disabled
// tooltip needs a real box to anchor + catch hover.
const anchorClass = computed(() => ({
  'm3-btn__anchor--active': showDisabledTooltip.value,
  'm3-btn__anchor--full': props.fullWidth,
}));

// Tooltip text: explicit prop wins (empty string "" suppresses auto-echo);
// falls back to the full label when ellipsis is active so truncated text
// stays reachable for mouse users.
const effectiveTooltip = computed<string | undefined>(() => {
  if (props.tooltip !== undefined) return props.tooltip || undefined;
  if (isEllipsized.value && props.label) return props.label;
  return undefined;
});

// Stable per-instance ID linking the button to its sr-only tooltip description
// (aria-describedby). useId() is Vue 3.5+ — returns a unique, hydration-stable string.
const tooltipDescId = useId();
const disabledDescId = useId();

// aria-describedby is only wired when the tooltip says something the
// accessible name does not. The collapsed header state and the ellipsis
// fallback both echo the label as the tooltip — describing a button with its
// own name makes screen readers announce it twice. The visual q-tooltip is
// unaffected; only the description linkage is dropped.
const fallthroughAttrs = useAttrs();
const accessibleName = computed(() =>
  String((fallthroughAttrs['aria-label'] as string | undefined) ?? props.label ?? '').trim(),
);
const tooltipAddsInformation = computed(() => {
  const tip = effectiveTooltip.value;
  return tip !== undefined && tip.trim() !== accessibleName.value;
});

// While the button is dead, the reason IS the description. Pointing at the
// label bubble instead would announce a hint about an action the user cannot
// take, ahead of the reason they cannot take it.
const describedById = computed<string | undefined>(() => {
  if (showDisabledTooltip.value) return disabledDescId;
  return tooltipAddsInformation.value ? tooltipDescId : undefined;
});

// Ref for programmatic show/hide — lets keyboard focus trigger the tooltip that
// Quasar normally only shows on hover.
type TooltipInstance = { show: () => void; hide: () => void };
const enabledTooltipRef = ref<TooltipInstance | null>(null);
const disabledTooltipRef = ref<TooltipInstance | null>(null);

function onFocusin(): void {
  if (effectiveTooltip.value) enabledTooltipRef.value?.show();
}

function onFocusout(): void {
  if (effectiveTooltip.value) enabledTooltipRef.value?.hide();
}

// SC 1.4.13 "Dismissable": Escape closes the bubble without moving the pointer
// or focus. Left to bubble, so a dialog that also closes on Escape still does.
function onEscape(): void {
  enabledTooltipRef.value?.hide();
  disabledTooltipRef.value?.hide();
}

// Touch has no hover, so the "why is this disabled?" reason would be
// unreachable there (hover hints are suppressed app-wide on coarse pointers —
// see @shared/tokens/css/_tooltips.scss; this bubble opts back in because its
// anchor is dead and tapping it can trigger nothing else). The disabled button
// carries pointer-events: none, so the tap lands on this wrapper. It closes
// itself since no mouseleave is coming.
const DISABLED_REASON_AUTO_HIDE_MS = 6000;
let disabledReasonTimer: ReturnType<typeof setTimeout> | null = null;

onBeforeUnmount(() => {
  if (disabledReasonTimer) clearTimeout(disabledReasonTimer);
});

function onAnchorTap(): void {
  if (!showDisabledTooltip.value) return;
  disabledTooltipRef.value?.show();
  if (disabledReasonTimer) clearTimeout(disabledReasonTimer);
  disabledReasonTimer = setTimeout(() => {
    disabledTooltipRef.value?.hide();
  }, DISABLED_REASON_AUTO_HIDE_MS);
}

function onClick(event: MouseEvent): void {
  if (props.loading || props.disable) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  // Hover-opened bubbles survive the click (Quasar binds only mouseenter /
  // mouseleave on the anchor); once the user has pressed, the hint is spent.
  enabledTooltipRef.value?.hide();
  emit('click', event);
}

// inheritAttrs: false — the template root is now a layout-transparent wrapper
// span (for the disabled-tooltip anchor), so fall-through attrs (e.g.
// WhiteButton's class="m3-btn--white", a call site's data-test / @mouseenter)
// are forwarded explicitly onto the inner button via `v-bind="$attrs"`, not
// onto the span. Class/style still merge with the button's own :class.
defineOptions({
  name: 'BaseButton',
  inheritAttrs: false,
});
</script>

<style lang="scss">
// Quasar spaces q-card-actions children through `.q-btn-item + .q-btn-item`
// sibling margins, which M3 buttons don't carry - without this rule adjacent
// buttons in any action row touch. `:where()` keeps specificity at zero so a
// component's own gap still wins.
:where(.q-card__actions) {
  gap: var(--spacing-sm, 8px);
}

// Layout-transparent wrapper around the button. `display: contents` means it
// generates no box, so flex/grid/`full-width` see the button directly — zero
// impact on the enabled common case. It only becomes a real box when it has to
// anchor (and catch hover for) the disabled-state tooltip, since a natively
// disabled button can't anchor its own.
.m3-btn__anchor {
  display: contents;

  &--active {
    display: inline-flex;
  }

  &--active.m3-btn__anchor--full {
    display: flex;
    width: 100%;
  }
}

.m3-btn {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1.5px solid transparent;
  border-radius: var(--btn-radius);
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-weight: var(--btn-font-weight);
  letter-spacing: var(--btn-letter-spacing);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  overflow: hidden;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color var(--btn-transition),
    border-color var(--btn-transition),
    color var(--btn-transition),
    box-shadow var(--btn-transition),
    transform var(--btn-transition-press);

  &:focus { outline: none; }

  &:focus-visible {
    outline: var(--btn-focus-ring-width) solid var(--q-primary);
    outline-offset: var(--btn-focus-ring-offset);
  }

  &:active:not(.m3-btn--disabled):not(.m3-btn--loading) {
    transform: scale(var(--btn-press-scale));
  }

  // ── Size scale ──────────────────────────────────────────────────────────
  // Icon sizes use a CSS variable (--m3-btn-icon-px) rather than the Quasar
  // :size prop. useButtonFit sets the variable when scaling; the per-tier
  // fallback activates when it is not set (the common, unscaled case).
  &--size-sm {
    height: var(--btn-height-sm);
    padding: 0 var(--spacing-md);
    font-size: var(--font-size-sm);
    .m3-btn__icon { font-size: var(--m3-btn-icon-px, 16px); }
  }
  &--size-md {
    height: var(--btn-height-md);
    padding: 0 var(--spacing-lg);
    font-size: var(--font-size-base);
    .m3-btn__icon { font-size: var(--m3-btn-icon-px, 18px); }
  }
  &--size-lg {
    height: var(--btn-height-lg);
    padding: 0 var(--spacing-xl);
    font-size: var(--font-size-lg);
    .m3-btn__icon { font-size: var(--m3-btn-icon-px, 22px); }
  }

  // ── Mobile: reduce horizontal padding so labels have more room ──────────
  // The gap / icon / font shrink via useButtonFit when overflow is detected;
  // these breakpoint rules lower the starting padding so the threshold is
  // reached less often (and buttons feel compact rather than padded on phones).
  @media (max-width: 599px) {
    &--size-sm { padding: 0 var(--spacing-sm); }
    &--size-md { padding: 0 var(--spacing-md); }
    &--size-lg { padding: 0 var(--spacing-lg); }
  }

  &--full-width { width: 100%; }
  &--pill       { border-radius: var(--btn-radius-pill); }

  // Icon-only buttons collapse to a square aspect (use the dedicated
  // IconButton component for richer hover treatment + circular shape).
  &--icon-only {
    aspect-ratio: 1;
    padding: 0;
    width: var(--btn-height-md);
  }
  &--icon-only.m3-btn--size-sm { width: var(--btn-height-sm); }
  &--icon-only.m3-btn--size-lg { width: var(--btn-height-lg); }

  // ── Disabled / loading ──────────────────────────────────────────────────
  &--disabled {
    opacity: 0.42;
    filter: saturate(0.6);
    cursor: not-allowed;
    pointer-events: none;
  }

  &--loading {
    cursor: progress;
    pointer-events: none;
  }

  // ── Content layer (label + icons) ───────────────────────────────────────
  // min-width: 0 down the flex chain lets the label shrink below its
  // intrinsic width, so useButtonFit's ellipsis fallback can engage when a
  // label overflows even at the minimum font size. No effect on the common
  // case — flex items only shrink when space actually runs out.
  &__content {
    position: relative;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: inherit;
    line-height: 1;
    min-width: 0;
    max-width: 100%;
  }

  &__label  { line-height: 1; min-width: 0; }
  &__icon   { flex-shrink: 0; line-height: 0; }

  // ── State layer (M3) — same-coloured overlay, opacity-driven ────────────
  &__state-layer {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: currentColor;
    opacity: 0;
    transition: opacity var(--btn-transition);
  }
  &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) .m3-btn__state-layer { opacity: var(--btn-state-hover); }
  &:focus-visible:not(.m3-btn--disabled):not(.m3-btn--loading) .m3-btn__state-layer { opacity: var(--btn-state-focus); }
  &:active:not(.m3-btn--disabled):not(.m3-btn--loading) .m3-btn__state-layer { opacity: var(--btn-state-press); }

  // ── Bouncing progress band (only mounted while loading) ─────────────────
  &__progress {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -50%;
    width: 50%;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--brand-gradient-start, var(--q-primary)) 28%, transparent) 30%,
      color-mix(in srgb, var(--brand-gradient-end,   var(--q-primary)) 36%, transparent) 50%,
      color-mix(in srgb, var(--brand-gradient-start, var(--q-primary)) 28%, transparent) 70%,
      transparent 100%
    );
    filter: blur(10px);
    animation: m3-btn-bounce 1.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
    will-change: transform;
  }

  // ============================================================================
  // PAINT VARIANTS — each pulls from semantic tokens (no hard-coded colours).
  // ============================================================================

  &--filled {
    background: var(--btn-filled-bg);
    color: var(--btn-filled-fg);
    box-shadow: var(--btn-filled-shadow);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      box-shadow: var(--btn-filled-shadow-hover);
    }
  }

  &--tonal {
    background: var(--btn-tonal-bg);
    color: var(--btn-tonal-fg);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-tonal-bg-hover);
    }
  }

  &--outlined {
    background: transparent;
    color: var(--btn-outlined-fg);
    border-color: var(--btn-outlined-border);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      border-color: var(--btn-outlined-border-hover);
      background: var(--btn-outlined-bg-hover);
    }
  }

  &--text {
    background: transparent;
    color: var(--btn-text-fg);
    border-color: transparent;

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-text-bg-hover);
    }
  }

  &--elevated {
    background: var(--btn-elevated-bg);
    color: var(--btn-elevated-fg);
    box-shadow: var(--btn-elevated-shadow);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      box-shadow: var(--btn-elevated-shadow-hover);
    }
  }

  &--destructive {
    background: var(--btn-destructive-bg);
    color: var(--btn-destructive-fg);
    box-shadow: var(--btn-destructive-shadow);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-destructive-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-destructive-bg);
    }
  }

  &--destructive-tonal {
    background: var(--btn-destructive-tonal-bg);
    color: var(--btn-destructive-tonal-fg);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-destructive-tonal-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-destructive-bg);
    }
  }

  &--success {
    background: var(--btn-success-bg);
    color: var(--btn-success-fg);
    box-shadow: var(--btn-success-shadow);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-success-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-success-bg);
    }
  }

  &--success-tonal {
    background: var(--btn-success-tonal-bg);
    color: var(--btn-success-tonal-fg);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-success-tonal-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-success-bg);
    }
  }

  &--warning {
    background: var(--btn-warning-bg);
    color: var(--btn-warning-fg);
    box-shadow: var(--btn-warning-shadow);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-warning-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-warning-bg);
    }
  }

  &--warning-tonal {
    background: var(--btn-warning-tonal-bg);
    color: var(--btn-warning-tonal-fg);

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-warning-tonal-bg-hover);
    }

    &:focus-visible {
      outline-color: var(--btn-warning-bg);
    }
  }

  &--glass {
    background: var(--btn-glass-bg);
    color: var(--btn-glass-fg);
    border-color: var(--btn-glass-border);
    backdrop-filter: blur(var(--btn-glass-blur));
    -webkit-backdrop-filter: blur(var(--btn-glass-blur));

    &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
      background: var(--btn-glass-bg-hover);
    }

    // Glass sits on dark backgrounds — invert the focus ring colour.
    &:focus-visible {
      outline-color: rgba(255, 255, 255, 0.85);
    }
  }
}

// ============================================================================
// TOOLTIP — dark M3 bubble (shared by BaseButton + IconButton). Quasar
// teleports q-tooltip to <body>, so this class lives at the top level (not
// nested under .m3-btn) to survive the portal.
// ============================================================================
.m3-btn__tooltip {
  padding: 6px 10px;
  border-radius: var(--radius-sm);
  background: var(--btn-tooltip-bg);
  color: var(--btn-tooltip-fg);
  font-size: var(--font-size-sm);
  font-weight: 500;
  line-height: 1.3;
  max-width: 240px;
  box-shadow: var(--btn-tooltip-shadow);
}

// Visually-hidden description span referenced by aria-describedby.
// Screen readers announce it when the button is focused; sighted users
// see the q-tooltip instead. Kept out of the visible layout.
.m3-btn__sr-desc {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// ============================================================================
// SPECIALTY MODIFIER CLASSES (used by WhiteButton, OutlineButton white prop)
// ============================================================================

// White CTA over a dark hero — high-contrast filled treatment.
.m3-btn--white {
  background: var(--btn-on-brand-bg) !important;
  color: var(--btn-on-brand-fg) !important;
  // SC 1.4.11: the variant is designed for a dark/brand band, but it is also
  // used on light page surfaces, where a white fill on #F5F7FA is 1.07:1 and
  // the control has no edge. A brand-blue outline gives it one everywhere and
  // is invisible against the fill it already sits on.
  border: 1px solid var(--btn-on-brand-fg) !important;
  box-shadow: 0 4px 20px rgba(255, 255, 255, 0.2) !important;

  &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
    box-shadow: 0 10px 40px rgba(255, 255, 255, 0.3) !important;
  }

  &:focus-visible { outline-color: #ffffff; }

  .m3-btn__state-layer { background: var(--q-primary); }
}

// WhatsApp brand modifier — for messaging surfaces where the WhatsApp
// green is a deliberate visual cue (familiar to users from the consumer
// app). Stack on `filled` to repaint the brand-gradient to WhatsApp green.
// App-level styling, not a generic intent — kept here next to other
// surface-modifier classes for consistency.
.m3-btn--whatsapp {
  background: var(--q-whatsapp, #118376) !important;
  // Both halves must come from the same app. ui defines --q-whatsapp per theme
  // (light teal / dark bright green) and pairs each with --on-whatsapp. An app
  // that defines NEITHER falls back to the light teal + white, which is 4.63:1
  // and correct - so do not add --on-whatsapp on its own anywhere, or a dark
  // label lands on the light-teal fallback fill (3.74:1).
  color: var(--on-whatsapp, #ffffff) !important;
  box-shadow: 0 6px 20px color-mix(in srgb, var(--q-whatsapp, #128C7E) 35%, transparent) !important;

  &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
    background: color-mix(in srgb, var(--q-whatsapp, #128C7E) 88%, #000) !important;
  }

  &:focus-visible { outline-color: var(--q-whatsapp, #128C7E); }
}

// Text/link/tonal on a dark surface (drawer header, dark banner, hero
// overlay). Repaints the text colour + hover state-layer to white. Stack
// on any variant — usually `text` or `tonal`. Distinct from --white (which
// is filled white CTA) and --outline-white (which is bordered).
.m3-btn--on-dark {
  color: #ffffff !important;

  &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
    background: rgba(255, 255, 255, 0.10) !important;
  }

  &:focus-visible { outline-color: #ffffff; }

  // The state-layer paints with currentColor (now white) — opacity-driven.
  .m3-btn__state-layer { background: #ffffff; }
}

// Outlined-on-dark (white border / white text).
.m3-btn--outline-white {
  background: transparent !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.7) !important;

  &:hover:not(.m3-btn--disabled):not(.m3-btn--loading) {
    border-color: #ffffff !important;
    background: rgba(255, 255, 255, 0.08) !important;
  }

  &:focus-visible { outline-color: #ffffff; }
}

// ============================================================================
// MOTION FALLBACK
// ============================================================================
@keyframes m3-btn-bounce {
  0%   { transform: translateX(0); }
  50%  { transform: translateX(300%); }
  100% { transform: translateX(0); }
}

@media (prefers-reduced-motion: reduce) {
  .m3-btn {
    transition: none;

    &:active:not(.m3-btn--disabled):not(.m3-btn--loading) {
      transform: none;
    }

    &__progress {
      animation: none;
      left: 25%;
      width: 50%;
      opacity: 0.55;
      filter: blur(14px);
    }

    &__state-layer { transition: none; }
  }
}
</style>
